import { createContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrentProfile } from '../../../api/services/profile';
import type { Profile } from '../../../api/services/profile/types';
import axiosInstance from '../../../api/axiosInstance';
import { useLogout } from '../hooks/auth-api.hooks';


export interface AuthContextType {
  user: Profile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => void;
  refetchProfile: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  // Initialize user state from localStorage
  const [user, setUser] = useState<Profile | null>(() => {
    const cachedAuth = localStorage.getItem('auth_state');
    if (cachedAuth) {
      try {
        const parsed = JSON.parse(cachedAuth);
        return parsed.user || null;
      } catch {
        return null;
      }
    }
    return null;
  });
  
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Get profile on mount - only fetch if we think we're logged in
  const { 
    data: profileData, 
    isLoading: isLoadingProfile, 
    error: profileError,
    refetch: refetchProfile
  } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      console.log('[AuthContext] Fetching profile...');
      try {
        const profile = await getCurrentProfile();
        console.log('[AuthContext] ✅ Profile fetch succeeded:', profile);
        return profile;
      } catch (error: any) {
        console.error('[AuthContext] ❌ Profile fetch failed:', {
          status: error?.response?.status,
          message: error?.message,
          code: error?.code,
        });
        throw error;
      }
    },
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: true,
  });

  // Logout mutation using the API hook
  const { mutate: logoutMutation } = useLogout();

  // Setup 401 interceptor to auto-logout with retry limit
  useEffect(() => {
    const interceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && !isLoggingOut) {
          console.log('401 Unauthorized - logging out user');
          setIsLoggingOut(true);
          setUser(null);
          queryClient.clear();
          navigate('/entrar');
          
          // Reset flag after navigation
          setTimeout(() => setIsLoggingOut(false), 100);
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axiosInstance.interceptors.response.eject(interceptor);
    };
  }, [navigate, queryClient, isLoggingOut]);

  // Update user when profile data changes
  useEffect(() => {
    if (profileData) {
      console.log('[AuthContext] Profile data received, updating user state:', profileData);
      setUser(profileData);
      // Cache the auth state
      localStorage.setItem('auth_state', JSON.stringify({ user: profileData }));
    }
  }, [profileData]);

  // Handle profile fetch error (401 means not authenticated)
  useEffect(() => {
    if (profileError) {
      const error = profileError as any;
      console.log('[AuthContext] Profile fetch error:', {
        status: error?.response?.status,
        message: error?.message,
      });
      if (error.response?.status === 401) {
        console.log('Profile fetch failed with 401 - user not authenticated');
        setUser(null);
        // Clear cached auth state
        localStorage.removeItem('auth_state');
      }
    }
  }, [profileError]);

  const logout = () => {
    console.log('[AuthContext] 🚪 Logout initiated');
    setIsLoggingOut(true);
    setUser(null);
    // Clear cached auth state
    localStorage.removeItem('auth_state');
    
    logoutMutation(undefined, {
      onSuccess: () => {
        console.log('[AuthContext] ✅ Logout successful, redirecting to /entrar');
        console.log('[AuthContext] Cookies on successful logout:', document.cookie);
        navigate('/entrar');
        setIsLoggingOut(false);
      },
      onError: (error) => {
        console.error('[AuthContext] ❌ Logout API failed, logging out locally anyway:', error);
        console.log('[AuthContext] Cookies on failed logout:', document.cookie);
        // Even if API fails, still log out locally
        navigate('/entrar');
        setIsLoggingOut(false);
      },
    });
  };

  const value: AuthContextType = {
    user,
    isLoading: isLoadingProfile,
    isAuthenticated: !!user,
    logout,
    refetchProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
